import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { CertificatePhoto } from '@/services/workersV2API';

type Props = {
  certificatePhotos: CertificatePhoto[];
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusColor = (
  status: string,
): 'theme' | 'danger' | 'warning' | 'neutral' => {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'theme';
    case 'rejected':
      return 'danger';
    case 'pending':
      return 'warning';
    default:
      return 'neutral';
  }
};

const CertificatePhotosCard = ({ certificatePhotos }: Props) => {
  if (!certificatePhotos || certificatePhotos.length === 0) {
    return (
      <Card title="Certificate Photos (0)">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">No certificate photos found</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card title={`Certificate Photos (${certificatePhotos.length})`}>
      {certificatePhotos.map((photo) => (
        <Card.Section key={photo.id}>
          <Stack gap={16} style={{ alignItems: 'flex-start' }}>
            {photo.image_url && (
              <a
                href={photo.image_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ flexShrink: 0 }}
              >
                <div
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: '#F3F4F6',
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#1976d2';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <img
                    src={photo.image_url}
                    alt={photo.skill_name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
              </a>
            )}
            <Stack vertical gap={12} style={{ flex: 1 }}>
              <Stack
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Body weight="semibold">{photo.skill_name}</Body>
                <Badge
                  label={
                    photo.status.charAt(0).toUpperCase() + photo.status.slice(1)
                  }
                  status={getStatusColor(photo.status)}
                />
              </Stack>

              {photo.type && (
                <Stack
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '6px',
                    display: 'inline-block',
                    width: 'fit-content',
                  }}
                >
                  <Small weight="semibold">{photo.type}</Small>
                </Stack>
              )}

              <Stack gap={16}>
                <Small color="lighter">
                  Uploaded: {formatDate(photo.created_at)}
                </Small>
                {photo.created_at !== photo.updated_at && (
                  <Small color="lighter">
                    Updated: {formatDate(photo.updated_at)}
                  </Small>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Card.Section>
      ))}
    </Card>
  );
};

export default CertificatePhotosCard;
